// LLM:METADATA
// :topology: [Domain:Navigation | Role:Composable | Pattern:BreadcrumbsBuilder]
// :relates-to: uses: "vue-router", "vuex", required-by: "App.vue"
// :rationale: "Global breadcrumbs system that builds hierarchical navigation trail from route and module data"
// :contract: pre: "Route and store available", post: "Returns computed breadcrumb items array", invariant: "Always includes Home as first item"
// :decision_cache: "Chose composable pattern for reactive, route-aware breadcrumbs [BREAD-001]"
// LLM:END

import { computed } from "vue"
import { useRoute } from "vue-router"
import { useStore } from "vuex"

/**
 * Breadcrumb composable that builds hierarchical navigation trail
 * @returns {Object} { items: Ref<Array<{title: string, url: string|null}>> }
 */
export function useBreadcrumbs() {
  const route = useRoute()
  const store = useStore()

  /**
   * LLM:METADATA
   * :topology: [Domain:Navigation | Role:BreadcrumbsBuilder | Pattern:Computed]
   * :relates-to: uses: "route", "store.getters", required-by: "BreadCrumbs component"
   * :rationale: "Builds breadcrumb items array based on current route and module data"
   * :contract: pre: "Route resolved", post: "Returns array of breadcrumb items", invariant: "First item is always Home"
   * LLM:END
   */
  const items = computed(() => {
    const path = route.path
    const name = route.name
    const crumbs = [{ title: "Главная", url: "/" }]

    // /modules - Module list page
    if (path === "/modules" || name === "module_list") {
      crumbs.push({ title: "Пакеты модулей", url: null })
      return crumbs
    }

    // /modules/:id - Module items page (show hierarchy: Pack -> Module)
    if (
      path.startsWith("/modules/") &&
      !path.includes("/add") &&
      !path.includes("/edit") &&
      !path.includes("/multiedit")
    ) {
      const moduleId = route.params.id
      const module = store.getters.getModuleWithPack?.(moduleId)

      if (module?.pack) {
        crumbs.push({ title: "Пакеты модулей", url: "/modules" })
        crumbs.push({
          title: module.pack.title,
          url: `/modules?pack=${module.pack.pack_id}`,
        })
      } else {
        // Fallback if no pack info
        crumbs.push({ title: "Пакеты модулей", url: "/modules" })
      }

      if (module?.module_title) {
        crumbs.push({ title: module.module_title, url: null })
      } else {
        crumbs.push({ title: "Модуль", url: null })
      }

      return crumbs
    }

    // /modules/:id/add - Add new module item
    if (path.match(/\/modules\/\d+\/add/)) {
      const moduleId = route.params.id
      const module = store.getters.getModuleWithPack?.(moduleId)

      if (module?.pack) {
        crumbs.push({ title: "Пакеты модулей", url: "/modules" })
        crumbs.push({
          title: module.pack.title,
          url: `/modules?pack=${module.pack.pack_id}`,
        })
        crumbs.push({ title: module.module_title, url: `/modules/${moduleId}` })
      } else {
        crumbs.push({ title: "Пакеты модулей", url: "/modules" })
        crumbs.push({
          title: module?.module_title || "Модуль",
          url: `/modules/${moduleId}`,
        })
      }

      crumbs.push({ title: "Добавить", url: null })
      return crumbs
    }

    // /modules/:id/edit/:itemid - Edit module item
    if (path.match(/\/modules\/\d+\/edit/)) {
      const moduleId = route.params.id
      const module = store.getters.getModuleWithPack?.(moduleId)

      if (module?.pack) {
        crumbs.push({ title: "Пакеты модулей", url: "/modules" })
        crumbs.push({
          title: module.pack.title,
          url: `/modules?pack=${module.pack.pack_id}`,
        })
        crumbs.push({ title: module.module_title, url: `/modules/${moduleId}` })
      } else {
        crumbs.push({ title: "Пакеты модулей", url: "/modules" })
        crumbs.push({
          title: module?.module_title || "Модуль",
          url: `/modules/${moduleId}`,
        })
      }

      crumbs.push({ title: "Редактирование", url: null })
      return crumbs
    }

    // /modules/:id/multiedit - Multi-edit module items
    if (path.match(/\/modules\/\d+\/multiedit/)) {
      const moduleId = route.params.id
      const module = store.getters.getModuleWithPack?.(moduleId)

      if (module?.pack) {
        crumbs.push({ title: "Пакеты модулей", url: "/modules" })
        crumbs.push({
          title: module.pack.title,
          url: `/modules?pack=${module.pack.pack_id}`,
        })
        crumbs.push({ title: module.module_title, url: `/modules/${moduleId}` })
      } else {
        crumbs.push({ title: "Пакеты модулей", url: "/modules" })
        crumbs.push({
          title: module?.module_title || "Модуль",
          url: `/modules/${moduleId}`,
        })
      }

      crumbs.push({ title: "Мультиредактирование", url: null })
      return crumbs
    }

    // /users - Users list
    if (path === "/users" || name === "users") {
      crumbs.push({ title: "Пользователи", url: null })
      return crumbs
    }

    // /users/add - Add user
    if (path === "/users/add" || name === "users_add") {
      crumbs.push({ title: "Пользователи", url: "/users" })
      crumbs.push({ title: "Добавить", url: null })
      return crumbs
    }

    // /users/:userid - Edit user
    if (path.match(/\/users\/\d+/) || name === "users_edit") {
      crumbs.push({ title: "Пользователи", url: "/users" })
      crumbs.push({ title: "Редактирование", url: null })
      return crumbs
    }

    // /pages - Pages list
    if (path === "/pages" || name === "pages") {
      crumbs.push({ title: "Страницы", url: null })
      return crumbs
    }

    // /pages/:pageid - Edit page
    if (path.match(/\/pages\/\d+/) || name === "pages_edit") {
      crumbs.push({ title: "Страницы", url: "/pages" })
      crumbs.push({ title: "Редактирование", url: null })
      return crumbs
    }

    // /reports - Reports
    if (path === "/reports" || name === "reports") {
      crumbs.push({ title: "Отчеты", url: null })
      return crumbs
    }

    // /access - Access landing page
    if (path === "/access" || name === "access") {
      crumbs.push({ title: "Доступ", url: null })
      return crumbs
    }

    // /access/roles - Access roles list
    if (path === "/access/roles" || name === "access_roles") {
      crumbs.push({ title: "Доступ", url: "/access" })
      crumbs.push({ title: "Роли доступа", url: null })
      return crumbs
    }

    // /access/roles/:id - Access role edit/new
    if (path.match(/^\/access\/roles\/[^/]+$/) || name === "access_role_edit") {
      const roleCrumbTitle =
        route.params.id === "new" ? "Новая роль" : "Редактирование роли"
      crumbs.push({ title: "Доступ", url: "/access" })
      crumbs.push({ title: "Роли доступа", url: "/access/roles" })
      crumbs.push({ title: roleCrumbTitle, url: null })
      return crumbs
    }

    // /access/admins - Access admin assignments
    if (path === "/access/admins" || name === "access_admins") {
      crumbs.push({ title: "Доступ", url: "/access" })
      crumbs.push({ title: "Назначение ролей", url: null })
      return crumbs
    }

    // /services/* - Services pages
    if (path.startsWith("/services/")) {
      const serviceNames = {
        sales_1c: "Продажи 1С",
        compare_1c: "Сравнение 1С",
        prices: "Цены",
        refresh: "Обновление",
        revaluation: "Переоценка",
      }
      const serviceKey = path.split("/")[2]
      const serviceTitle = serviceNames[serviceKey] || "Сервис"
      crumbs.push({ title: "Сервисы", url: "/services" })
      crumbs.push({ title: serviceTitle, url: null })
      return crumbs
    }

    // Default: just home
    return crumbs
  })

  return { items }
}
